import {
  CreateOrderState,
  createOrderState,
} from "@/atoms/order/create-order-state";
import { profileState } from "@/atoms/user/profile-state";
import {
  DeliveryMessageType,
  deliveryMessageTypeDisplayNames,
} from "@/common/enum/delivery-message-type";
import {
  EmailDomainType,
  emailDomainTypeDisplayNames,
} from "@/common/enum/email-domain-type";
import { TextInputColor } from "@/common/enum/text-input-color";
import { formatPhoneNumber } from "@/common/util/format-phone-number";
import { CreatePartnerOrderStateError } from "@/page/order/PartnerCreateOrder/page";
import { Label, Radio, TextInput } from "flowbite-react";
import { useEffect, useState } from "react";
import { Address } from "react-daum-postcode";
import { useRecoilState } from "recoil";
import { AddressSearchButton } from "../button/AddressSearchButton";
import { CustomHelperText } from "../CustomHelperText";
import { DeliveryMessageDropdown } from "../dropdown/DeliveryMessageDropdown";
import { EmailDomainDropdown } from "../dropdown/EmailDomainDropdown";
import { TextInputLabel } from "../label/TextInputLabel";
import { Body14Regular } from "../typography/Body14Regular";

interface Props {
  aggregateErrors: CreatePartnerOrderStateError;
}

export const PartnerDeliveryAddressInputPanel: React.FC<Props> = ({
  aggregateErrors,
}) => {
  const [profile, setProfile] = useRecoilState(profileState);
  const [payload, setPayload] =
    useRecoilState<CreateOrderState>(createOrderState);

  const [emailDomainType, setEmailDomainType] = useState<EmailDomainType>(
    EmailDomainType.CUSTOM
  );
  const [deliveryMessageType, setDeliveryMessageType] =
    useState<DeliveryMessageType>(DeliveryMessageType.CUSTOM);

  const [isDefaultDeliveryAddress, setIsDefaultDeliveryAddress] =
    useState(true);

  const handleEmailDomainChange = (type: EmailDomainType) => {
    setEmailDomainType(type);
    if (type !== EmailDomainType.CUSTOM) {
      setPayload((prev) => ({
        ...prev,
        receiverEmailDomain: emailDomainTypeDisplayNames[type],
      }));
    } else {
      setPayload((prev) => ({
        ...prev,
        receiverEmailDomain: "",
      }));
    }
  };

  const handleDeliveryMessageChange = (type: DeliveryMessageType) => {
    setDeliveryMessageType(type);
    if (type !== DeliveryMessageType.CUSTOM) {
      setPayload((prev) => ({
        ...prev,
        receiverMemo: deliveryMessageTypeDisplayNames[type],
      }));
    } else {
      setPayload((prev) => ({
        ...prev,
        receiverMemo: "",
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPayload((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const formattedValue = formatPhoneNumber(event.target.value);

    setPayload({
      ...payload,
      receiverPhone: formattedValue,
    });
  };

  const setAddress = (address: Address) => {
    setPayload({
      ...payload,
      receiverRoad: address.roadAddress,
      receiverJibun: address.jibunAddress,
      receiverPostalCode: address.zonecode,
    });
  };

  useEffect(() => {
    if (isDefaultDeliveryAddress) {
      setPayload({
        ...payload,
        receiverName: profile.name,
        receiverPhone: profile.phone,
        receiverEmailId: profile.email.split("@")[0],
        receiverEmailDomain: profile.email.split("@")[1],
        receiverRoad: profile.road,
        receiverJibun: profile.jibun,
        receiverAddressDetail: profile.detailAddress,
        receiverPostalCode: profile.postalCode,
        receiverMemo: "",
      });
    } else {
      setPayload({
        ...payload,
        receiverName: "",
        receiverPhone: "",
        receiverEmailId: "",
        receiverEmailDomain: "",
        receiverRoad: "",
        receiverJibun: "",
        receiverAddressDetail: "",
        receiverPostalCode: "",
        receiverMemo: "",
      });
    }
  }, [isDefaultDeliveryAddress]);

  return (
    <div className="flex flex-col px-[24px] py-[16px]  bg-primary-white rounded-lg border-[1px] border-semantic-divider">
      <div className="flex gap-[20px]">
        <div>
          <TextInputLabel required>받는 사람</TextInputLabel>
          <TextInput
            className="w-[434px]"
            placeholder="받는 사람을 입력해주세요"
            name="receiverName"
            value={payload.receiverName}
            onChange={handleInputChange}
            color={TextInputColor.GRAY}
          />
        </div>
        <div>
          <TextInputLabel required>휴대전화</TextInputLabel>
          <TextInput
            className="w-[434px]"
            placeholder="휴대전화를 입력해주세요"
            name="receiverPhone"
            value={payload.receiverPhone}
            onChange={handlePhoneNumberChange}
            color={
              !!aggregateErrors.phone
                ? TextInputColor.FAILURE
                : TextInputColor.GRAY
            }
          />
          <CustomHelperText
            isError={!!aggregateErrors.phone}
            text={aggregateErrors.phone}
          />
        </div>
        <div className="w-[434px]">
          <TextInputLabel required>이메일</TextInputLabel>
          <div className="flex items-center gap-[8px]">
            <TextInput
              className="w-[132px]"
              placeholder="아이디"
              name="receiverEmailId"
              value={payload.receiverEmailId}
              onChange={handleInputChange}
              color={
                !!aggregateErrors.emailId
                  ? TextInputColor.FAILURE
                  : TextInputColor.GRAY
              }
            />
            <Body14Regular className="text-semantic-text-body">@</Body14Regular>
            <TextInput
              className="w-[132px]"
              placeholder="이메일 선택"
              name="receiverEmailDomain"
              value={
                emailDomainType === EmailDomainType.CUSTOM
                  ? payload.receiverEmailDomain
                  : emailDomainTypeDisplayNames[
                      payload.receiverEmailDomain as EmailDomainType
                    ]
              }
              onChange={handleInputChange}
              color={TextInputColor.GRAY}
              disabled={emailDomainType !== EmailDomainType.CUSTOM} // CUSTOM이 아닐 때 비활성화
            />
            <EmailDomainDropdown
              emailDomainType={emailDomainType}
              handleEmailDomainChange={handleEmailDomainChange}
            />
          </div>
          <CustomHelperText
            isError={!!aggregateErrors.emailId}
            text={aggregateErrors.emailId}
          />
        </div>
      </div>
      <div className="flex items-end gap-[8px] mt-[20px]">
        <div>
          <TextInputLabel required>주소지</TextInputLabel>
          <div className="flex gap-[10px]">
            <TextInput
              required
              className="w-[325px]"
              placeholder="우편번호"
              value={payload.receiverPostalCode}
              disabled
            />
            <AddressSearchButton setAddress={setAddress} />
          </div>
        </div>
        <div>
          <TextInput
            required
            className="w-[312px]"
            placeholder="기본 주소"
            value={payload.receiverRoad || payload.receiverJibun}
            disabled
          />
        </div>
        <div>
          <TextInput
            required
            className="w-[312px]"
            placeholder="나머지 주소 (선택 입력 가능)"
            name="receiverAddressDetail"
            value={payload.receiverAddressDetail}
            onChange={handleInputChange}
          />
        </div>
        <div>
          <TextInputLabel>배송 메세지</TextInputLabel>
          <DeliveryMessageDropdown
            deliveryMessageType={deliveryMessageType}
            handleDeliveryMessageChange={handleDeliveryMessageChange}
          />
        </div>
      </div>
      <fieldset className="flex justify-between mt-[8px]">
        <div className="flex gap-[20px]">
          <div className="flex items-center gap-[8px]">
            <Radio
              id="isDefaultDeliveryAddress-yes"
              name="isDefaultDeliveryAddress"
              value="YES"
              checked={isDefaultDeliveryAddress}
              onChange={() => setIsDefaultDeliveryAddress(true)}
            />
            <Label htmlFor="isDefaultDeliveryAddress-yes">
              회원 정보와 동일
            </Label>
          </div>
          <div className="flex items-center gap-[8px]">
            <Radio
              id="isDefaultDeliveryAddress-no"
              name="isDefaultDeliveryAddress"
              value="NO"
              checked={!isDefaultDeliveryAddress}
              onChange={() => setIsDefaultDeliveryAddress(false)}
            />
            <Label htmlFor="isDefaultDeliveryAddress-no">새로운 배송지</Label>
          </div>
        </div>
        <TextInput
          className="w-[311px]"
          placeholder="배송 메시지"
          name="receiverMemo"
          value={
            deliveryMessageType === DeliveryMessageType.CUSTOM
              ? payload.receiverMemo
              : deliveryMessageTypeDisplayNames[
                  payload.receiverMemo as DeliveryMessageType
                ]
          }
          onChange={handleInputChange}
          color={TextInputColor.GRAY}
          disabled={deliveryMessageType !== DeliveryMessageType.CUSTOM} // CUSTOM이 아닐 때 비활성화
        />
      </fieldset>
    </div>
  );
};

// TODO 나머지 유효성 검사?
